// Whether a Google campaign has Glitch work waiting to reach Google Ads (#6612). Shared by the campaigns sidebar's
// status icon and the Deploy button, so the two can never disagree. A campaign found in Google Ads and not yet
// adopted cannot be edited in Glitch, so nothing it carries is waiting to deploy, and it is never "ready to go
// live": it already runs in Google Ads. Its approval flags were created DRAFT before #6612, which read as both.
import { CAMPAIGN_STATE, type CampaignData } from '@/lib/types';

type PublishFields = Pick<
  CampaignData,
  'state' | 'origin' | 'managed_at' | 'first_published_at' | 'budget_state' | 'assets_state' | 'settings_state'
>;

/** A campaign found in Google Ads that has not been adopted into Glitch, and so is read-only here. */
function isUnadoptedExternal(campaign: Pick<CampaignData, 'origin' | 'managed_at'>): boolean {
  return campaign.origin === 'external' && !campaign.managed_at;
}

/** A LIVE campaign with a budget, assets or settings change made in Glitch and not yet deployed. */
export function hasUndeployedChanges(campaign: PublishFields): boolean {
  if (isUnadoptedExternal(campaign)) return false;
  return (
    campaign.state === CAMPAIGN_STATE.LIVE &&
    (campaign.budget_state === 'draft' || campaign.assets_state === 'draft' || campaign.settings_state === 'draft')
  );
}

/** A campaign Glitch built that has never gone live. Never true for one found in Google Ads. */
export function isAwaitingFirstPublish(campaign: Pick<CampaignData, 'origin' | 'first_published_at'>): boolean {
  return campaign.origin !== 'external' && !campaign.first_published_at;
}

/**
 * A paused campaign Glitch offers to resume: one Glitch built that has gone live before. A campaign found in Google
 * Ads is run there, so it is resumed there, never from Glitch, adopted or not (#6612). Pausing stays available.
 */
export function canResumeInGlitch(campaign: Pick<CampaignData, 'state' | 'origin' | 'first_published_at'>): boolean {
  return campaign.state === CAMPAIGN_STATE.PAUSED && campaign.origin !== 'external' && !!campaign.first_published_at;
}
